import * as Api from '@ViewModels';
import { getDefaultDateStringValue, getDefaultTimeStringValue } from '../../../../models/utils/dateUtils';
import { getDiscountName } from '../../../../models/utils/stringUtils';
import { Button } from '../../../../web/components-v2/Button';
import {
	formatPredictionReasonTimestamp,
	getWillRenewPredictionReasonCategoryTitle,
} from '../../../components/WillRenewPredictionSelect/reasonCategories';
import { HandwrittenCardBalanceDisplay } from '../../../containers/account/AccountDetails/HandwrittenCardBalanceDisplay';
import { isPaidAccount, isTrialExpired } from '../accountOverviewFormatters';
import { OverviewHoverPanel, OverviewHoverRow } from './OverviewHoverPanel';
import { renewalInfoPanelStyles as s } from './renewalInfoPanelStyles';

/**
 * Which flavour of renewal detail an account has, if any — the single condition both the panel's
 * branches and the caller's info-affordance gate read. They have to agree: a rendered-null element is
 * still truthy, so a separately-maintained predicate would silently put an icon on an empty card.
 */
export type RenewalInfoKind = 'paid' | 'trial' | null;

export const renewalInfoKind = (account?: Api.IAccount): RenewalInfoKind => {
	if (isPaidAccount(account)) {
		return 'paid';
	}
	if (account?.planDetails?.billingType === Api.BillingType.Trial) {
		return 'trial';
	}
	return null;
};

export const hasRenewalInfo = (account?: Api.IAccount): boolean => renewalInfoKind(account) !== null;

/**
 * Trial copy for the renewal info panel: whether the trial can still be extended, or — once it has
 * been — when and by how long (legacy parity, all four wordings).
 */
const TrialRenewalInfo = ({ account }: { account: Api.IAccount }) => {
	const trialExtension = account.planDetails?.trialExtension;
	const expired = isTrialExpired(account);

	if (!trialExtension) {
		return (
			<span>
				{expired
					? 'Your trial has expired, but you may extend it for 7, 14, or 30 days from today.'
					: 'You may extend your trial by 7, 14, or 30 days.'}
			</span>
		);
	}

	return (
		<span>
			{trialExtension.createdDate && !expired
				? `On ${getDefaultDateStringValue(trialExtension.createdDate)} your trial was extended by ${trialExtension.durationInDays} days.`
				: `Your trial was extended by ${trialExtension.durationInDays} days.`}
		</span>
	);
};

/**
 * The Billing card's "Renewal Info" hover panel — a port of the legacy `renewalInfoContent`.
 *
 * Paid accounts get next-bill figures, any applied discount, the cancellation on file (reason and
 * when it was requested), the will-renew prediction reason, and the handwritten-card balance when
 * that feature is on. Trial accounts get the trial-extension copy instead. Anything else renders
 * nothing, which leaves the field with no info affordance at all.
 */
export const RenewalInfoPanel = ({
	account,
	onStopCancellation,
}: {
	account: Api.IAccount;
	/** Opens the Stop Cancellation flow. Omit to render the panel without that action. */
	onStopCancellation?: () => void;
}) => {
	const planDetails = account.planDetails;
	const predictionReason = account.additionalInfo?.willRenewPredictionReason;
	const hasPredictionReason = !!predictionReason?.category || !!predictionReason?.detail;
	const kind = renewalInfoKind(account);

	if (kind === 'paid') {
		return (
			<OverviewHoverPanel title='Renewal Info'>
				<OverviewHoverRow label='Next Billing Date'>
					{getDefaultDateStringValue(planDetails?.nextBill?.date)}
				</OverviewHoverRow>
				<OverviewHoverRow label='Next Billing Amount'>{`$${planDetails?.nextBill?.amount}`}</OverviewHoverRow>
				{planDetails?.discountApplied != null && (
					<OverviewHoverRow label='Discount'>{getDiscountName(planDetails.discountApplied)}</OverviewHoverRow>
				)}
				{!!planDetails?.pendingCancellation && (
					<>
						<OverviewHoverRow label='Cancel Reason Given'>{planDetails.pendingCancellation.reason}</OverviewHoverRow>
						<OverviewHoverRow label='Cancel Requested At'>
							{`${getDefaultDateStringValue(planDetails.pendingCancellation.createdDate)} ${getDefaultTimeStringValue(
								planDetails.pendingCancellation.createdDate
							)}`}
						</OverviewHoverRow>
						{!!onStopCancellation && (
							<Button variant='secondary' styles={[s.stopCancellation]} onClick={onStopCancellation}>
								Stop Cancellation
							</Button>
						)}
					</>
				)}
				{hasPredictionReason && (
					<>
						<OverviewHoverRow label='Prediction Reason'>
							{getWillRenewPredictionReasonCategoryTitle(predictionReason.category)}
						</OverviewHoverRow>
						{!!predictionReason.detail && (
							<OverviewHoverRow label='Prediction Detail'>{predictionReason.detail}</OverviewHoverRow>
						)}
						{!!predictionReason.createdDate && (
							<OverviewHoverRow label='Prediction Updated'>
								{formatPredictionReasonTimestamp(predictionReason.createdDate)}
							</OverviewHoverRow>
						)}
					</>
				)}
				{!!account.features?.handwrittenCards?.enabled && <HandwrittenCardBalanceDisplay account={account} />}
			</OverviewHoverPanel>
		);
	}

	if (kind === 'trial') {
		return (
			<OverviewHoverPanel>
				<TrialRenewalInfo account={account} />
			</OverviewHoverPanel>
		);
	}

	return null;
};
